import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getInstruments, uploadContent } from '../../../features/sessions/sessionAudioContent/sliceUserContents';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

const BG_DIALOG     = '#1c1c24';
const BG_INPUT      = '#24242e';
const BRAND_RED     = '#c0392b';
const TEXT_PRIMARY  = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE = '1px solid rgba(255,255,255,0.08)';

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: TEXT_PRIMARY,
        bgcolor: BG_INPUT,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },

        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px #1c1c24 inset`,
            WebkitTextFillColor: '#f4f4f6',
            caretColor: '#f4f4f6',
        },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
    },
    '& .MuiInputLabel-root': { color: TEXT_SECONDARY },
    '& .MuiInputLabel-root.Mui-focused': { color: '#f4f4f6', fontWeight: 700 },
};

export default function MyContentsUploadForm() {
    const currentUser  = useSelector((state: RootState) => state.session.currentUser);
    const authHeaders  = useSelector((state: RootState) => state.session.authHeaders);
    const instrumentsForm = useSelector((state: RootState) => state.sessionUserContents.instrumentsForm);
    const dispatch = useDispatch<AppDispatch>();
    const { userNicknameRoute } = useParams();

    const [open, setOpen] = useState(false);
    const [titleForm, setTitleForm] = useState('');
    const [descriptionForm, setDescriptionForm] = useState('');
    const [contentTypeIdForm, setContentTypeIdForm] = useState(1);
    const [downloadAbledForm, setDownloadAbledForm] = useState('true');
    const [instrumentSelected, setInstrumentSelected] = useState(1);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    useEffect(() => {
        dispatch(getInstruments());
    }, []);

    function handleReset() {
        setTitleForm('');
        setDescriptionForm('');
        setContentTypeIdForm(1);
        setDownloadAbledForm('true');
        setInstrumentSelected(1);
        setSelectedFile(null);
    }

    function handleClose() {
        handleReset();
        setOpen(false);
    }

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!selectedFile) {
            toast.error('Please select a file.');
            return;
        }
        const requestBody = {
            title: titleForm,
            description: descriptionForm,
            contentTypeId: contentTypeIdForm,
            file: selectedFile,
            downloadAbled: downloadAbledForm,
            instrumentId: instrumentSelected,
        };
        const response = await dispatch(uploadContent({ authHeaders, currentUser, requestBody }));
        if (response.meta.requestStatus === 'fulfilled') {
            handleClose();
        } else if (response.payload?.errors?.length > 0) {
            response.payload.errors.forEach((msg: string) => toast.error(msg));
        }
    }

    return (
        <>
            <Button
                variant='contained'
                startIcon={<AddIcon />}
                onClick={() => setOpen(true)}
                sx={{
                    bgcolor: BRAND_RED,
                    color: '#fff',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#a93226' },
                }}
            >
                Add Content
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth='sm'
                PaperProps={{ sx: { bgcolor: BG_DIALOG, color: TEXT_PRIMARY, border: BORDER_SUBTLE } }}
            >
                <DialogTitle sx={{ color: TEXT_PRIMARY, borderBottom: BORDER_SUBTLE, pb: 1.5 }}>
                    Post Content
                </DialogTitle>

                <Box component='form' onSubmit={handleSubmit}>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>

                        <TextField
                            label='Title'
                            value={titleForm}
                            onChange={(e) => setTitleForm(e.target.value)}
                            size='small'
                            required
                            fullWidth
                            sx={inputSx}
                        />

                        <TextField
                            label='Description'
                            value={descriptionForm}
                            onChange={(e) => setDescriptionForm(e.target.value)}
                            multiline
                            rows={3}
                            size='small'
                            fullWidth
                            sx={inputSx}
                        />

                        <FormControl size='small' fullWidth sx={inputSx}>
                            <InputLabel>Instrument</InputLabel>
                            <Select
                                label='Instrument'
                                value={instrumentSelected}
                                onChange={(e) => setInstrumentSelected(Number(e.target.value))}
                                MenuProps={{ PaperProps: { sx: { bgcolor: BG_INPUT, color: TEXT_PRIMARY } } }}
                            >
                                {instrumentsForm?.map((inst) => (
                                    <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel sx={{ color: TEXT_SECONDARY, fontSize: 13, mb: 0.5 }}>Content Type</FormLabel>
                            <RadioGroup row value={contentTypeIdForm} onChange={(e) => setContentTypeIdForm(Number(e.target.value))}>
                                <FormControlLabel value={1} control={<Radio size='small' sx={{ color: BRAND_RED, '&.Mui-checked': { color: BRAND_RED } }} />} label={<Typography variant='body2' color={TEXT_PRIMARY}>Audio</Typography>} />
                                <FormControlLabel value={2} disabled control={<Radio size='small' />} label={<Typography variant='body2' color={TEXT_SECONDARY}>Text</Typography>} />
                                <FormControlLabel value={3} disabled control={<Radio size='small' />} label={<Typography variant='body2' color={TEXT_SECONDARY}>Video</Typography>} />
                            </RadioGroup>
                        </FormControl>

                        <FormControl>
                            <FormLabel sx={{ color: TEXT_SECONDARY, fontSize: 13, mb: 0.5 }}>Download</FormLabel>
                            <RadioGroup row value={downloadAbledForm} onChange={(e) => setDownloadAbledForm(e.target.value)}>
                                <FormControlLabel value='true' control={<Radio size='small' sx={{ color: BRAND_RED, '&.Mui-checked': { color: BRAND_RED } }} />} label={<Typography variant='body2' color={TEXT_PRIMARY}>Enabled</Typography>} />
                                <FormControlLabel value='false' control={<Radio size='small' sx={{ color: BRAND_RED, '&.Mui-checked': { color: BRAND_RED } }} />} label={<Typography variant='body2' color={TEXT_PRIMARY}>Disabled</Typography>} />
                            </RadioGroup>
                        </FormControl>

                        <Button
                            component='label'
                            variant='outlined'
                            size='small'
                            sx={{ borderColor: 'rgba(255,255,255,0.2)', color: TEXT_SECONDARY, textTransform: 'none', alignSelf: 'flex-start', '&:hover': { borderColor: TEXT_PRIMARY, color: TEXT_PRIMARY } }}
                        >
                            {selectedFile ? selectedFile.name : 'Choose file…'}
                            <input type='file' hidden onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)} />
                        </Button>

                    </DialogContent>

                    <DialogActions sx={{ borderTop: BORDER_SUBTLE, px: 3, py: 1.5, gap: 1 }}>
                        <Button onClick={handleClose} sx={{ color: TEXT_SECONDARY, textTransform: 'none' }}>
                            Cancel
                        </Button>
                        <Button
                            type='submit'
                            variant='contained'
                            sx={{ bgcolor: BRAND_RED, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#a93226' } }}
                        >
                            Upload
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </>
    );
}
